import { apiRequest } from './api';

/**
 * A random id this browser makes up once, used only to count a look at a
 * listing as ONE person per day rather than one per refresh.
 *
 * It is not a login, not an address and not a fingerprint: 18 random
 * characters and nothing else. The server hashes it together with the listing
 * id before storing, so the stored value differs from home to home and cannot
 * be used to follow somebody around the site.
 */
const KEY = 'ns.visitor';

function visitorId(): string | null {
  try {
    let id = window.localStorage.getItem(KEY);
    if (!id || !/^[A-Za-z0-9_-]{16,64}$/.test(id)) {
      const bytes = new Uint8Array(18);
      crypto.getRandomValues(bytes);
      id = Array.from(bytes, (b) => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'[b % 64]).join('');
      window.localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // No storage: do not count. An uncountable visitor is better than a fresh
    // id on every page, which would count every refresh as a new person.
    return null;
  }
}

/** Count this look. Silent on failure — a view count must never break a page. */
export function recordListingView(listingId: string) {
  const visitor = visitorId();
  if (!visitor) return;
  apiRequest(`/api/v1/discover/${listingId}/view`, { method: 'POST', body: { visitor } })
    .catch(() => { /* not the visitor's problem */ });
}

/**
 * The same, for a property for sale (migration 131).
 *
 * A separate call rather than a shared one with a type parameter: the two write
 * to different tables through different definer functions, and the day one of
 * them needs to stop counting, it should be possible to stop it without reading
 * the other's callers.
 */
export function recordSaleView(saleListingId: string) {
  const visitor = visitorId();
  if (!visitor) return;
  apiRequest(`/api/v1/sales/${saleListingId}/view`, { method: 'POST', body: { visitor } })
    .catch(() => { /* not the visitor's problem */ });
}
